import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import type { BranchRow } from "@/modules/branches/domain/types";
import { branchesApi } from "@/modules/branches/presentation/api/client";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

const FILTER_ALL = "__all__" as const;

function toParam(v: string): string | undefined {
  return v === FILTER_ALL || !v ? undefined : v;
}

export function MovementsFilter() {
  const nav = useNavigate({ from: "/app/inventory/movements" });
  const search = useSearch({ from: "/app/inventory/movements" }) as {
    branchId?: string;
    reason?: string;
    dateFrom?: string;
    dateTo?: string;
    offset?: number;
    limit?: number;
  };

  const { data: branchesData } = useQuery({
    queryKey: ["branches", "list", { limit: 200, offset: 0 }],
    queryFn: () => branchesApi.list({ limit: 200, offset: 0 }),
  });

  const branches: BranchRow[] = branchesData?.data ?? [];
  const branchOptions = [
    { value: FILTER_ALL, label: "ທຸກສາຂາ" },
    ...branches.map((b) => ({ value: b.id, label: b.name })),
  ];
  const reasonOptions = [
    { value: FILTER_ALL, label: "ທຸກປະເພດ" },
    { value: "PURCHASE", label: "ຮັບເຂົ້າ" },
    { value: "SALE", label: "ຂາຍ" },
    { value: "WASTE", label: "ສູນເສຍ" },
    { value: "ADJUST", label: "ປັບຍອດ" },
    { value: "TRANSFER_IN", label: "ໂອນເຂົ້າ" },
    { value: "TRANSFER_OUT", label: "ໂອນອອກ" },
  ];

  const hasFilter =
    !!search.branchId ||
    !!search.reason ||
    !!search.dateFrom ||
    !!search.dateTo;

  const update = (next: Partial<typeof search>) => {
    nav({ search: { ...search, ...next, offset: 0 } });
  };

  const clearFilters = () => {
    nav({ search: { offset: search.offset ?? 0, limit: search.limit ?? 20 } });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b px-4 pb-2">
      <SimpleSelect
        placeholder="ສາຂາ"
        value={search.branchId ?? FILTER_ALL}
        onValueChange={(v) => update({ branchId: toParam(v ?? "") })}
        options={branchOptions}
        className="w-40"
      />
      <SimpleSelect
        placeholder="ປະເພດ"
        value={search.reason ?? FILTER_ALL}
        onValueChange={(v) => update({ reason: toParam(v ?? "") })}
        options={reasonOptions}
        className="w-40"
      />
      <input
        type="date"
        className="rounded-md border bg-background px-3 py-2 text-sm"
        value={search.dateFrom ?? ""}
        onChange={(e) => update({ dateFrom: e.target.value || undefined })}
      />
      <input
        type="date"
        className="rounded-md border bg-background px-3 py-2 text-sm"
        value={search.dateTo ?? ""}
        onChange={(e) => update({ dateTo: e.target.value || undefined })}
      />
      {hasFilter && (
        <button
          type="button"
          onClick={clearFilters}
          className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
        >
          <XIcon className="size-4" />
          ລ້າງຕົວກອງ
        </button>
      )}
    </div>
  );
}
